import React, { useState, useEffect } from 'react';
import { launchersApi } from '../../../../utils/api/launchers';
import defaultLauncherThumb from '../../../../assets/images/default_launcher_thumb.png';

/**
 * LauncherThumbnail - Displays launcher icon with API fetch and fallback
 * 
 * Props:
 * - launcherId: number - Launcher ID for fetching thumbnail
 * - iconUrl: string - Direct URL to icon (optional)
 * - className: string - Additional CSS classes
 */
const LauncherThumbnail = ({ launcherId, iconUrl, className = '' }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadThumbnail = async () => {
      // If iconUrl is a data URL (preview) or full http URL, use it directly
      if (iconUrl && (iconUrl.startsWith('data:') || iconUrl.startsWith('http'))) {
        setThumbnailUrl(iconUrl);
        return;
      }

      // No launcher ID means we can't fetch - use default
      if (!launcherId) {
        setThumbnailUrl(defaultLauncherThumb);
        return;
      }

      // Fetch thumbnail via API (iconUrl is just a storage path, not usable directly)
      try {
        setIsLoading(true);
        setHasError(false);
        
        const thumbnail = await launchersApi.getThumbnail(launcherId);
        
        if (!mounted) return;

        if (thumbnail) {
          objectUrl = URL.createObjectURL(thumbnail);
          setThumbnailUrl(objectUrl);
        } else {
          // No thumbnail found - use default
          setThumbnailUrl(defaultLauncherThumb);
        }
      } catch (err) {
        console.warn(`Thumbnail load failed for launcher ${launcherId}:`, err);
        if (mounted) {
          setThumbnailUrl(defaultLauncherThumb);
          setHasError(true);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [launcherId, iconUrl]);

  // Loading state - show default with pulse animation
  if (isLoading) {
    return (
      <img
        src={defaultLauncherThumb}
        alt="Loading..."
        className={`object-cover opacity-50 animate-pulse ${className}`}
      />
    );
  }

  // Show thumbnail (or default if no thumbnail/error)
  return (
    <img
      src={thumbnailUrl || defaultLauncherThumb}
      alt="Launcher thumbnail"
      className={`object-cover ${className}`}
      onError={(e) => {
        e.target.src = defaultLauncherThumb;
        setHasError(true);
      }}
    />
  );
};

export default React.memo(LauncherThumbnail);
